import { generateDatadogTrackingProps } from 'toolkit/environment/environment/userMonitoring.util';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { Box, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import ReactPlayer from 'react-player';
import { TemporaryLinkResponse } from '@act/shared/data-transfer-objects';
import { useAudioPlayer } from './useAudioPlayer.hook';

interface AudioPlayerProps {
  fileId?: string;
  tempFile?: TemporaryLinkResponse;
  alwaysShowDownload?: boolean;
  mimeType?: string;
}

/**
 * Plays a stored audio file from a signed URL.
 *
 * A refreshed link does not replace the media source while the current link
 * is still valid, so a voicemail keeps playing instead of restarting when
 * the short-lived URL is renewed.
 */
const AudioPlayer = ({
  fileId,
  tempFile,
  alwaysShowDownload,
  mimeType,
}: AudioPlayerProps) => {
  const player = useAudioPlayer(fileId, tempFile, mimeType);

  return (
    <div className="app">
      {player.showPlayer && player.playerUrl && (
        <ReactPlayer
          ref={player.playerRef}
          url={player.playerUrl}
          className="react-player"
          width="0px"
          height="0px"
          playing={player.playing}
          onReady={player.onReady}
          onPlay={player.onPlay}
          onPause={player.onPause}
          onPlaybackRateChange={player.onPlaybackRateChange}
          onError={player.onError}
          onProgress={player.onProgress}
          onDuration={player.onDuration}
        />
      )}
      <Tooltip
        title={
          player.disabled
            ? 'Unsupported file type. Download file to listen'
            : ''
        }
      >
        <Box onMouseDown={player.onControlMouseDown}>
          <Box
            display="grid"
            gridTemplateColumns="min-content 1fr"
            alignItems="center"
            columnGap="8px"
          >
            <Box>
              <IconButton
                {...generateDatadogTrackingProps(
                  'audio-player-icon-button-play-pause'
                )}
                disabled={player.disabled}
                aria-label={player.playing ? 'pause' : 'play'}
                onClick={player.onTogglePlay}
              >
                {player.playing ? <PauseCircleIcon /> : <PlayCircleIcon />}
              </IconButton>
            </Box>

            <Box>
              <Slider
                disabled={player.disabled}
                aria-label="Volume"
                step={0.00000001}
                min={0}
                max={0.999999}
                value={player.played}
                onMouseDown={player.onSeekMouseDown}
                onChange={player.onSeekChange}
                onMouseUp={player.onSeekMouseUp}
              />

              {(alwaysShowDownload || player.disabled) && (
                <a
                  {...generateDatadogTrackingProps('audio-player-a-1')}
                  href={player.downloadUrl ?? undefined}
                  download
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconButton
                    {...generateDatadogTrackingProps(
                      'audio-player-icon-button-download'
                    )}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </a>
              )}
            </Box>

            <Box>{/* Purposely left empty for layout purposes*/}</Box>
            {!player.disabled && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="bodySmall">{player.timePlayed}</Typography>
                <Typography variant="bodySmall">
                  {player.timeRemaining}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Tooltip>
    </div>
  );
};

export default AudioPlayer;
